import { PieChartOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsGraphParams,
  MembershipAnalyticsGraphOnboardingAnswers,
} from '@models/membership/MembershipAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { Entity } from '@utils/Entities'
import {
  Card,
  Empty,
  Flex,
  Segmented,
  Space,
  Spin,
  theme,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'
import { Pie, PieChart, ResponsiveContainer, Sector } from 'recharts'

const { Text } = Typography

const AnswerChart = () => {
  const { token } = theme.useToken()
  const color = token.colorPrimary
  const { selectedDates, selectedMembershipPrograms } = useAnalytics()

  const [error, setError] = useState<string>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(1)
  const [selectedFormQuestionId, setSelectedFormQuestionId] = useState<number>()
  const [selectedChart, setSelectedChart] = useState<'pie-chart' | 'bar-chart'>(
    'pie-chart'
  )
  const [chartData, setChartData] = useState<
    MembershipAnalyticsGraphOnboardingAnswers[]
  >([])

  const fetchData = async () => {
    if (!selectedFormQuestionId) {
      return
    }

    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setChartData([])
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams: FetchMembershipAnalyticsGraphParams = {
      entity: 'membership',
      kind: 'onboarding-answers',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      questionId: selectedFormQuestionId,
      programIds: selectedMembershipPrograms,
      filter: 'members',
    }

    try {
      const { data } =
        await fetchAnalyticsGraph<MembershipAnalyticsGraphOnboardingAnswers[]>(
          fetchParams
        )
      setChartData(data || [])

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setSelectedFormQuestionId(undefined)
    setChartData([])
  }, [selectedMembershipPrograms])

  useEffect(() => {
    fetchData()
  }, [selectedDates, selectedFormQuestionId, selectedMembershipPrograms])

  const allValuesAreZero = chartData.every(({ value }) => value === 0)

  const renderActiveShape = (props: any) => {
    const RADIAN = Math.PI / 180
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props
    const sin = Math.sin(-RADIAN * midAngle)
    const cos = Math.cos(-RADIAN * midAngle)
    const sx = cx + (outerRadius + 10) * cos
    const sy = cy + (outerRadius + 10) * sin
    const mx = cx + (outerRadius + 30) * cos
    const my = cy + (outerRadius + 30) * sin
    const ex = mx + (cos >= 0 ? 1 : -1) * 22
    const ey = my
    const textAnchor = cos >= 0 ? 'start' : 'end'

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
        >
          {' '}
          {`${(percent * 100).toFixed(2)}%`}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
        >
          {`${value}`}
        </text>
      </g>
    )
  }

  return (
    <Card>
      <Flex align="end" gap={8}>
        <SBAsyncSelect
          key={selectedMembershipPrograms?.join(',')}
          style={{ width: '100%' }}
          placeholder="Select question"
          entity={Entity.membershipFormQuestion}
          disabled={isLoading}
          onChange={(value, option) => {
            console.log('Selected question:', value, option)
            setSelectedFormQuestionId(value)
          }}
          value={selectedFormQuestionId}
          searchFieldName="item"
          initialQuery={{
            pagination: { current: 1, pageSize: 100 },
            filters: {
              withOptions: [true],
              ...(selectedMembershipPrograms && {
                programs: selectedMembershipPrograms,
              }),
            },
          }}
        />
        <Segmented
          defaultValue={selectedChart}
          options={[
            {
              value: 'pie-chart',
              label: <PieChartOutlined />,
            },
            {
              value: 'bar-chart',
              label: <UnorderedListOutlined />,
            },
          ]}
          onChange={(e) => {
            setSelectedChart(e as any)
          }}
        />
      </Flex>
      <Flex align="center" justify="center" style={{ minHeight: 400 }}>
        {isLoading && <Spin />}
        {(allValuesAreZero || chartData.length === 0 || error) &&
          !isLoading &&
          selectedFormQuestionId && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
          )}
        {!selectedFormQuestionId && !isLoading && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Please select a question to view the answers"
          />
        )}
        {!allValuesAreZero &&
          chartData &&
          !isLoading &&
          !error &&
          selectedFormQuestionId && (
            <>
              {selectedChart === 'pie-chart' && (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      activeIndex={activeIndex}
                      activeShape={renderActiveShape}
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={90}
                      outerRadius={150}
                      fill={color}
                      dataKey="value"
                      onMouseEnter={(_, index) => setActiveIndex(index)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}

              {selectedChart === 'bar-chart' && (
                <Space
                  orientation="vertical"
                  style={{ width: '100%', placeSelf: 'start', padding: 20 }}
                >
                  {chartData.map((item) => (
                    <Text key={item.name}>{`${item.name}: ${item.value}`}</Text>
                  ))}
                </Space>
              )}
            </>
          )}
      </Flex>
      <Text type="secondary" italic>
        Only active members are included in the displayed data.
      </Text>
    </Card>
  )
}

export default AnswerChart
